import { Divider, Filter } from '@vrplatform/components';
import type { SelectItem } from '@vrplatform/components';
import { day, hasValue } from '@vrplatform/utils';
import { Group } from '@mantine/core';
import { useMemo } from 'react';
import { CustomFilter } from '../../../components';
import { useTrialBalanceTableFilter } from './useTrialBalanceTableFilter';

export const TrialBalanceTableFilter = () => {
  return (
    <Group>
      <Search />
      <Date />
      <Book />
      <Classifications />
      <Type />
      <Party />
      <Divider orientation="vertical" />
      <View />
    </Group>
  );
};

const Search = () => {
  const { filter, setFilter } = useTrialBalanceTableFilter();

  return (
    <Filter.Search
      value={filter.search || ''}
      setValue={(v) => setFilter({ search: v })}
    />
  );
};

const Date = () => {
  const { filter, setFilter } = useTrialBalanceTableFilter();

  const value = useMemo(() => {
    if (!filter.date)
      return `${day().startOf('month').yyyymmdd()}...${day().endOf('month').yyyymmdd()}`;

    return filter.date;
  }, [filter.date]);

  return (
    <Filter.Date
      value={value}
      clearable={false}
      setValue={(value) => {
        setFilter({
          date: value
            ?.filter(hasValue)
            .map((date) => day(date).yyyymmdd())
            .join('...'),
        });
      }}
    />
  );
};

const Book = () => {
  'use memo';

  const { filter, setFilter } = useTrialBalanceTableFilter();

  return (
    <CustomFilter.Book
      book={filter.ledger}
      setBook={(ledger) => setFilter({ ledger })}
    />
  );
};

const Classifications = () => {
  'use memo';

  const { filter, setFilter } = useTrialBalanceTableFilter();

  return (
    <CustomFilter.Classification
      type="multiple"
      classification={filter.classifications?.filter(hasValue)}
      setClassification={(v) => setFilter({ classifications: v })}
    />
  );
};

const Party = () => {
  'use memo';

  const { filter, setFilter } = useTrialBalanceTableFilter();

  return (
    <CustomFilter.Party
      party={filter.party}
      setParty={(party) => setFilter({ party })}
    />
  );
};
const Type = () => {
  'use memo';

  const { filter, setFilter } = useTrialBalanceTableFilter();

  return (
    <CustomFilter.AccountType
      type={filter.type}
      setType={(type) => setFilter({ type })}
    />
  );
};

const View = () => {
  'use memo';

  const { filter, setFilter } = useTrialBalanceTableFilter();

  const options = useMemo<SelectItem<NonNullable<(typeof filter)['view']>>[]>(
    () => [
      {
        label: 'Non-zero',
        value: 'non-zero',
      },
    ],
    []
  );

  const value = options.find((i) => i.value === filter.view) ?? null;

  return (
    <Filter.Select
      value={value}
      label="View"
      setValue={(v) =>
        setFilter({
          view: v?.value,
        })
      }
      type="single"
      data={{
        options,
      }}
      popoverWidth={300}
    />
  );
};
